import React from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '../common/CloseIcon';
import './AuthHeader.scss';

/**
 * Props for the shared auth header. Both the title and the back control are
 * optional so a host can render a bare close row, which is what the auth modal
 * needs: each screen already renders its own heading in the body.
 */
interface AuthHeaderProps {
  title?: string;
  onBack?: () => void;
  onClose: () => void;
}

/**
 * Shared header row for every auth screen: back chevron, centred title, close
 * (JAA-273). The chevron's viewBox is cropped to the glyph, so it fills the
 * same 22px box the close cross does instead of floating small inside it. Matches the Figma login frame (3483:59324) and replaces the
 * per-screen header blocks the old modal duplicated. The back slot is always
 * reserved so the title stays optically centred whether or not it is shown.
 */
const AuthHeader: React.FC<AuthHeaderProps> = ({ title, onBack, onClose }) => {
  const { t } = useTranslation();

  return (
    <div className="auth-header">
      {onBack ? (
        <button
          className="auth-header__action"
          onClick={onBack}
          type="button"
          aria-label={t('signupLoginModal.backAria')}
        >
          <svg
            width="22"
            height="22"
            viewBox="7 3 10 18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      ) : (
        <span className="auth-header__action" aria-hidden="true" />
      )}
      <span className="auth-header__title">{title}</span>
      <button
        className="auth-header__action"
        onClick={onClose}
        type="button"
        aria-label={t('signupLoginModal.closeAria')}
      >
        <CloseIcon size={22} color="currentColor" />
      </button>
    </div>
  );
};

export default AuthHeader;
